import clsx from 'clsx'
import compact from 'lodash/compact'
import last from 'lodash/last'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC, useEffect, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { Measure } from '@Assets/SVG'
import { DrawerPageSync } from '@Components/Drawer/Page'
import { Checkbox } from '@Components/FormElements/UI/Checkbox'
import { ImageV4 } from '@Components/Image'
import { NativeModalBody, NativeModalFooter, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { SizeTable } from '@Components/Product/SizeGuide/SizeTable'
import { Button } from '@CoreComponents/Buttons/Button'
import { PromptSelectVariant } from '@Domains/Cart/Components/Prompt/Dialog'
import { NecklaceOption } from '@Domains/Product/Features/Display/NecklaceOption/NecklaceOption'
import { DrawerDataFragment, PagePresentation, Product } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

import styles from './SizePromptV2.module.scss'

export interface SizePromptProps {
  loading: boolean
  onSubmit: (variant: Option) => void
  product: Product
  promptSelectVariants?: PromptSelectVariant[]
}

type Option = {
  isInStock: boolean
  key: string
  label: string
  quantity: number
  variantID?: string
}

const MostPopularSize: Record<string, string> = {
  belts: 'cms:MOST_POPULAR_SIZE_BELTS',
  boxers: 'cms:MOST_POPULAR_SIZE_BOXERS',
  bracelets: 'cms:MOST_POPULAR_SIZE_BRACELETS',
  gloves: 'cms:MOST_POPULAR_SIZE_GLOVES',
  necklaces: 'cms:MOST_POPULAR_SIZE_NECKLACE',
  rings: 'cms:MOST_POPULAR_SIZE_RINGS',
}

const NONE_SELECTED = 'NONE_SELECTED'

const getKey: (productVariant) => string = productVariant => productVariant?.size.value.key || ''
export const SizePromptV2: FC<SizePromptProps> = ({
  loading,
  onSubmit,
  product,
  promptSelectVariants = [],
}) => {
  const { t } = useTranslation()
  const { locale = 'en-GB' } = useRouter()
  const [currentStepIndex, isLastStep, showStepper] = useMemo(() => {
    const show = promptSelectVariants && promptSelectVariants?.length > 1
    const i = promptSelectVariants!.findIndex(p => p.productID === product.id)
    const isLast = last(promptSelectVariants)?.productID === product.id
    return [i, isLast, show]
  }, [product.id, promptSelectVariants])

  const [variant, setVariant] = useState<Option>({
    key: NONE_SELECTED,
    label: '',
    isInStock: true,
    quantity: 0,
    variantID: undefined,
  })

  // reset stepper when product id changes
  useEffect(() => {
    if (!showStepper) {
      return
    }

    setVariant((prev) => {
      if (prev.key === NONE_SELECTED) {
        return prev
      }

      return {
        key: NONE_SELECTED,
        label: '',
        isInStock: true,
        quantity: 0,
        variantID: undefined,
      }
    })
  }, [product.id, showStepper])

  const [showError, setShowError] = useState(false)

  const noneSelected = useMemo(() => locale && variant.key === NONE_SELECTED, [locale, variant])
  const sizeNotInStock = useMemo(() => !variant?.isInStock, [variant])

  const variants = useMemo(() => product.variants || [], [product.variants])
  const availableVariantOptions: Option[] = useMemo(
    () => compact(map(
      variants || [],
      (productVariant) => {
        const isInStock = !!productVariant.availability?.isInStock
        const quantity = productVariant.availability?.quantity || 0

        if (
          'restock' in productVariant
          // @ts-ignore
          && 'value' in productVariant.restock
          // @ts-ignore
          && 'key' in productVariant.restock.value
          && productVariant.restock.value.key !== 'enabled'
          && (!isInStock || quantity === 0)
        ) {
          return null
        }

        return {
          key: getKey(productVariant),
          /* @ts-ignore */
          label: productVariant?.size.value.label[locale] || productVariant?.size.value.key,
          isInStock,
          quantity,
          variantID: productVariant.id || undefined,
        }
      },
    )),
    [locale, variants],
  )

  useEffect(() => {
    ampli.promptOpen({ source_id: showStepper ? 'size_prompt_grouped' : 'size_prompt' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const deactivateAddToCart = loading || noneSelected || !variant || sizeNotInStock
  const sizeGuideItems = product.sizeGuideItems || []

  return (
    <>
      <NativeModalHeader>
        {t('products:CHOOSE_SIZE')}
        {showStepper && ` (${currentStepIndex + 1}/${promptSelectVariants?.length})`}
      </NativeModalHeader>
      <NativeModalBody>
        <div className={styles.sizePrompt}>
          {showStepper && !!promptSelectVariants[currentStepIndex]?.product?.primaryAsset && (
            <div className={styles.currentStep}>
              <div className={styles.imageWrapper}>
                <ImageV4 src={promptSelectVariants[currentStepIndex].product!.primaryAsset!.url} width={85} />
              </div>
              <p>{promptSelectVariants[currentStepIndex].product?.name}</p>
            </div>
          )}
          {product.sizeGuide && (
            <DrawerPageSync
              page={product.sizeGuide as DrawerDataFragment}
              presentation={PagePresentation.Sidebar}
              sourceID="size_prompt"
              triggerRender={props => (
                <Button
                  {...props}
                  className=""
                  fillWidth
                  innerSpacing="medium"
                  style={{ flexShrink: 0, marginBottom: '12px' }}
                  theme="white"
                  type="button"
                >
                  <Measure />
                  {t('cms:SIZE_GUIDE_LABEL')}
                </Button>
              )}
            >
              {sizeGuideItems.length !== 0 && (
                <div>
                  <SizeTable items={product.sizeGuideItems || []} />
                </div>
              )}
            </DrawerPageSync>
          )}
          {product.type && MostPopularSize[product.type] && (
            <p className={styles.mostPopular}>
              {t(MostPopularSize[product.type])}
            </p>
          )}
          <div className={styles.content}>
            {map(availableVariantOptions, option => (
              <button
                key={option.key}
                className={clsx(styles.option, !option.isInStock && styles.unavailable, !product.sizeGuide && styles.noSizeGuide)}
                disabled={!option.isInStock}
                onClick={() => setVariant(prev => (option.isInStock ? option : prev))}
                type="button"
              >
                {product.type === 'necklaces' && (
                  <NecklaceOption
                    disabled={!option.isInStock}
                    size={option.key.replaceAll(' ', '')}
                  />
                )}
                <div>
                  <Checkbox
                    aria-label={option.label}
                    checked={option?.variantID === variant.variantID}
                    disabled={!option.isInStock}
                    id={`sizeSelect${option.key}`}
                    label=""
                    onClick={() => setVariant(prev => (option.isInStock ? option : prev))}
                    rounded
                    wrapperClassName={styles.checkbox}
                  >
                    <div className={`${product.type === 'necklaces' ? styles.hasImage : ''}`}>
                      <span className={styles.label}>{option.label}</span>
                    </div>

                  </Checkbox>
                  {(option.isInStock && option.quantity < 10) && (
                    <span className={styles.stock}>
                      {`${option.quantity} ${t('products:PP_LEFT_IN_STOCK_LABEL')}`}
                    </span>
                  )}
                  {!option.isInStock && (
                    <span className={styles.stock}>
                      {t('cms:OUT_OF_STOCK')}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </NativeModalBody>
      <NativeModalFooter className={clsx(styles.footer)}>
        {deactivateAddToCart && showError && (
          <p className={styles.error}>{t('cms:USER_SELECT_OVERLAY_SIZE_TITLE')}</p>
        )}
        <Button
          disabled={deactivateAddToCart}
          fillWidth
          innerSpacing="medium"
          loading={loading}
          onClick={() => {
            if (deactivateAddToCart) {
              setShowError(true)
            } else {
              setShowError(false)
              onSubmit(variant)
            }
          }}
          type="button"
        >
          {!showStepper && t('cms:ADD_TO_CART')}
          {showStepper && isLastStep && t('cms:ADD_TO_CART')}
          {showStepper && !isLastStep && t('common:PAGINATION_NEXT_PAGE')}
        </Button>
      </NativeModalFooter>
    </>
  )
}
